<template>
  <Portal>
    <Modal
      class="configuration-modal"
      data-testid="paid-events-configuration-modal"
      :is-open="isOpen"
      :show-back-button="false"
      size="md"
      title=""
      @close="handleClose"
    >
      <ModalStepTransition class="configuration-modal__transition">
        <div
          v-if="currentStep === PaidEventsConfigurationSteps.Selection"
          key="selection-step"
          class="configuration-modal__content"
        >
          <HeadingDisplay
            class="configuration-modal__heading"
            :title="formTitle"
            :subtitle="formDescription"
            size="small"
          />
          <SelectionBox
            :aria-label="formDescription"
            type="single"
            :value="option"
            :options="formOptions"
            @change="handleChangeOption"
          />
        </div>
        <div
          v-else-if="currentStep === PaidEventsConfigurationSteps.Status"
          key="status-step"
          class="configuration-modal__content"
        >
          <HelperViewStatus
            class="configuration-modal__helper"
            :title="statusMessage.title"
            :description="statusMessage.description"
            :status="statusMessage.status"
            :button-loading="isLoading"
            :button-label="statusMessage.buttonLabel"
            @click="handleSubmit"
          />
        </div>
      </ModalStepTransition>
      <template #buttonContainer>
        <ModalStepTransition>
          <ButtonContainer
            v-if="currentStep === PaidEventsConfigurationSteps.Selection"
          >
            <template #button-primary>
              <ButtonPrimary
                label="Confirmar"
                :disabled="!hasOptionSelected"
                :loading="isLoading"
                @click="handleSubmit"
              />
            </template>
          </ButtonContainer>
          <!-- This div is a workaround to prevent a bug with transition -->
          <div v-else />
        </ModalStepTransition>
      </template>
    </Modal>
  </Portal>
</template>

<script setup lang="ts">
import { ref, computed, inject, ComputedRef, watch } from 'vue';

import {
  Modal,
  ButtonContainer,
  ButtonPrimary,
  HeadingDisplay,
  SelectionBox,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { ModalStepTransition } from '@/modules/common/components/ModalStepTransition';
import { Portal } from '@/modules/common/components/Portal';

import { PaidEventsConfigurationSteps } from './types';
import {
  PortfolioPaidEventConfiguration,
  updatePaidEventsDestination
} from '@/modules/wealth/services/portfolio';

interface Props {
  isOpen: boolean;
  paidEventsConfiguration?: PortfolioPaidEventConfiguration;
}
const props = defineProps<Props>();

interface Emits {
  (e: 'close'): void;
}
const emit = defineEmits<Emits>();

const currentStep = ref(PaidEventsConfigurationSteps.Selection);
const option = ref('');
const isLoading = ref(false);
const statusMessage = ref({
  status: 'negative',
  title: 'Houve um problema ao configurar proventos',
  description: 'Aguarde uns instantes e tente novamente',
  buttonLabel: ''
});

const portfolioId = inject('portfolioId') as ComputedRef<string>;
const getAboutData = inject('getAboutData') as Function;

watch(
  () => props.isOpen,
  () => {
    option.value = props.paidEventsConfiguration?.form.selectedIndex || '';
    if (props.paidEventsConfiguration?.enabled === false && props.isOpen) {
      currentStep.value = PaidEventsConfigurationSteps.Status;
      statusMessage.value = {
        status: 'information',
        title: props.paidEventsConfiguration?.blockedStatus?.title || '',
        description:
          props.paidEventsConfiguration?.blockedStatus?.description || '',
        buttonLabel: ''
      };
    }
  },
  { immediate: true }
);

const isPaidEventsConfigurationEnabled = computed(() => {
  return !!props.paidEventsConfiguration?.enabled;
});

const formTitle = computed(() => {
  return props.paidEventsConfiguration?.form?.title || '';
});

const formDescription = computed(() => {
  return props.paidEventsConfiguration?.form?.description || '';
});

const formOptions = computed(() => {
  return (
    props.paidEventsConfiguration?.form?.options.map(option => {
      return {
        title: option.formatted,
        value: option.value
      };
    }) || []
  );
});

const hasOptionSelected = computed(() => {
  return !!option.value;
});

const handleClose = () => {
  if (isPaidEventsConfigurationEnabled.value)
    currentStep.value = PaidEventsConfigurationSteps.Selection;
  option.value = '';

  emit('close');
};

const handleSubmit = async () => {
  if (!hasOptionSelected.value) return;

  try {
    isLoading.value = true;

    const response = await updatePaidEventsDestination({
      portfolioId: portfolioId.value,
      paidEventDestination: option.value
    });

    statusMessage.value = {
      ...response,
      buttonLabel: ''
    };

    getAboutData();
  } catch {
    statusMessage.value = {
      status: 'negative',
      title: 'Houve um problema ao configurar proventos',
      description: 'Aguarde uns instantes e tente novamente',
      buttonLabel: 'Tentar novamente'
    };
  } finally {
    currentStep.value = PaidEventsConfigurationSteps.Status;
    isLoading.value = false;
  }
};

const handleChangeOption = (value: string) => {
  option.value = value;
};
</script>

<style lang="less" scoped>
.configuration-modal {
  :deep(.modal-content) {
    padding: 0 @size-spacing-x1000 @size-spacing-x1000 @size-spacing-x1000;
  }

  &__transition {
    width: 100%;
  }

  &__content {
    width: 100%;
    text-align: left !important;
  }

  &__heading {
    margin-bottom: @size-spacing-x600;
  }

  &__helper {
    margin: 0 auto;
  }
}
</style>
